<template>
    <main class="announcement-tool">
        <section class="announcement-editor">
            <div class="container-fluid tool-container">
                <div class="tool-heading">
                    <span>Duyuru aracı</span>
                    <h1>Duyuru Görseli Oluştur</h1>
                    <p>WhatsApp, Facebook ve Instagram paylaşımları için telefonda okunaklı portre PNG duyuru hazırlayın.</p>
                </div>

                <div class="tool-layout">
                    <form class="tool-form" @submit.prevent="downloadAsPng">
                        <div>
                            <label for="baslik" class="form-label">Başlık</label>
                            <input id="baslik" v-model="title" type="text" class="form-control" placeholder="Önemli Duyuru" />
                        </div>

                        <div>
                            <label for="mesaj" class="form-label">Mesaj</label>
                            <textarea
                                id="mesaj"
                                v-model="rawMessage"
                                class="form-control"
                                rows="7"
                                placeholder="Duyuru metnini buraya yazın."
                            />
                        </div>

                        <div class="form-grid">
                            <div>
                                <label for="yer" class="form-label">Yer</label>
                                <input id="yer" v-model="yer" type="text" class="form-control" placeholder="Dernek binası" />
                            </div>
                            <div>
                                <label for="tarih" class="form-label">Tarih</label>
                                <input id="tarih" v-model="tarih" type="text" class="form-control" placeholder="Gün / saat" />
                            </div>
                        </div>

                        <div>
                            <label for="iletisim" class="form-label">İletişim</label>
                            <input id="iletisim" v-model="iletisim" type="text" class="form-control" placeholder="0507 921 7404" />
                        </div>

                        <div class="tool-actions">
                            <button type="submit" class="btn btn-primary">
                                <i class="fa fa-download me-2"></i>PNG Olarak İndir
                            </button>
                        </div>
                    </form>

                    <div class="preview-shell">
                        <div class="preview-note">
                            <i class="fa fa-mobile-screen-button"></i>
                            1080 x 1350 portre paylaşım formatı
                        </div>
                        <div ref="posterFrame" class="poster-scale">
                            <div ref="captureArea" class="announcement-poster" :style="{ '--poster-scale': posterScale }">
                                <div class="poster-pattern"></div>
                                <div class="poster-ribbon poster-ribbon-one"></div>
                                <div class="poster-ribbon poster-ribbon-two"></div>

                                <section class="poster-content">
                                    <h2>{{ normalizedTitle }}</h2>
                                    <div class="poster-message-box">
                                        <p :class="['poster-message', messageLengthClass, { empty: !rawMessage.trim() }]">
                                            {{ normalizedMessage }}
                                        </p>
                                    </div>
                                </section>

                                <section v-if="infoCount" :class="['poster-info', `count-${infoCount}`]">
                                    <div v-if="yer.trim()">
                                        <small>Yer</small>
                                        <span>
                                            <i class="fa fa-location-dot"></i>
                                            <strong>{{ yer }}</strong>
                                        </span>
                                    </div>
                                    <div v-if="tarih.trim()">
                                        <small>Tarih / Saat</small>
                                        <span>
                                            <i class="fa fa-calendar-days"></i>
                                            <strong>{{ tarih }}</strong>
                                        </span>
                                    </div>
                                    <div v-if="iletisim.trim()">
                                        <small>İletişim</small>
                                        <span>
                                            <i class="fa fa-phone-alt"></i>
                                            <strong>{{ iletisim }}</strong>
                                        </span>
                                    </div>
                                </section>

                                <footer class="poster-footer">
                                    <img src="/img/logo.jpg" alt="Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği Logosu">
                                    <div>
                                        <span>Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği</span>
                                        <strong>Kütük No: 06-158-130</strong>
                                    </div>
                                </footer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import html2canvas from 'html2canvas'

const captureArea = ref(null)
const posterFrame = ref(null)
const posterScale = ref('0.52')
const title = ref('Önemli Duyuru')
const rawMessage = ref('')
const tarih = ref('')
const yer = ref('')
const iletisim = ref('0507 921 7404')

const normalizedTitle = computed(() => title.value.trim() || 'Önemli Duyuru')
const normalizedMessage = computed(() => rawMessage.value.trim() || 'Duyuru metnini yazdığınızda burada paylaşım görseli olarak görünecek.')
const infoCount = computed(() => [yer.value, tarih.value, iletisim.value].filter((item) => item.trim()).length)
const messageLengthClass = computed(() => {
    const length = rawMessage.value.trim().length
    if (length > 520) return 'tiny'
    if (length > 380) return 'very-long'
    if (length > 260) return 'long'
    if (length > 130) return 'medium'
    return 'short'
})

let resizeObserver

const updatePosterScale = () => {
    if (!posterFrame.value) return
    const width = posterFrame.value.getBoundingClientRect().width
    posterScale.value = String(Math.min(width / 1080, 1))
}

onMounted(() => {
    updatePosterScale()
    resizeObserver = new ResizeObserver(updatePosterScale)
    if (posterFrame.value) resizeObserver.observe(posterFrame.value)
})

onBeforeUnmount(() => {
    resizeObserver?.disconnect()
})

const downloadAsPng = async () => {
    if (!captureArea.value) return

    const previousTransform = captureArea.value.style.transform
    captureArea.value.style.transform = 'none'

    let canvas
    try {
        canvas = await html2canvas(captureArea.value, {
            backgroundColor: null,
            scale: 1,
            useCORS: true,
            width: 1080,
            height: 1350,
            windowWidth: 1080,
            windowHeight: 1350
        })
    } finally {
        captureArea.value.style.transform = previousTransform
    }

    const link = document.createElement('a')
    const date = new Date().toISOString().slice(0, 10)
    link.download = `ankara-agrililar-duyuru-${date}.png`
    link.href = canvas.toDataURL('image/png')
    link.click()
}
</script>

<style scoped>
.announcement-tool {
    background: #f5f7fb;
    min-height: 100vh;
}

.announcement-editor {
    padding: 52px 0 88px;
}

.tool-container {
    max-width: 1760px;
    padding-left: 28px;
    padding-right: 28px;
}

.tool-heading {
    max-width: 860px;
    margin-bottom: 28px;
}

.tool-heading span {
    color: var(--primary);
    font-size: 13px;
    font-weight: 900;
    text-transform: uppercase;
}

.tool-heading h1 {
    font-size: clamp(32px, 5vw, 54px);
    font-weight: 900;
    letter-spacing: 0;
    margin: 8px 0 10px;
}

.tool-heading p {
    color: #666565;
    font-size: 17px;
    margin: 0;
}

.tool-layout {
    display: grid;
    grid-template-columns: minmax(340px, 460px) minmax(720px, 1fr);
    gap: 28px;
    align-items: start;
}

.tool-form,
.preview-shell {
    background: #fff;
    border: 1px solid rgba(15, 23, 43, .08);
    border-radius: 8px;
    box-shadow: 0 18px 45px rgba(15, 23, 43, .08);
}

.tool-form {
    display: grid;
    gap: 18px;
    padding: 22px;
}

.tool-form label {
    color: var(--dark);
    font-weight: 800;
}

.tool-form .form-control {
    border-radius: 6px;
    min-height: 48px;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

.tool-actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
}

.tool-actions .btn {
    min-height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.preview-shell {
    padding: 18px;
    overflow: auto;
}

.preview-note {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #555;
    background: #f5f7fb;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 800;
    padding: 8px 12px;
    margin-bottom: 14px;
}

.preview-note i {
    color: var(--primary);
}

.poster-scale {
    width: min(100%, 720px);
    aspect-ratio: 4 / 5;
    margin: 0 auto;
}

.announcement-poster {
    position: relative;
    width: 1080px;
    height: 1350px;
    overflow: hidden;
    color: var(--dark);
    background: #fffdf8;
    transform: scale(var(--poster-scale));
    transform-origin: top left;
}

.poster-pattern,
.poster-ribbon {
    position: absolute;
    pointer-events: none;
}

.poster-pattern {
    inset: 0;
    background:
        radial-gradient(circle at 50% 0%, rgba(254, 161, 22, .16) 0%, rgba(254, 161, 22, .06) 30%, rgba(255, 255, 255, 0) 58%),
        linear-gradient(155deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 74%, rgba(15, 23, 43, .04) 74%, rgba(15, 23, 43, .04) 100%);
    opacity: 1;
}

.poster-ribbon {
    left: 0;
    right: 0;
    height: 12px;
    background: linear-gradient(90deg, var(--primary) 0 34%, var(--dark) 34% 100%);
    opacity: 1;
    transform: none;
}

.poster-ribbon-one {
    top: 0;
}

.poster-ribbon-two {
    bottom: 0;
    height: 10px;
    background: linear-gradient(90deg, var(--dark) 0 72%, var(--primary) 72% 100%);
    opacity: 1;
}

.poster-content,
.poster-info,
.poster-footer {
    position: relative;
    z-index: 2;
}

.poster-content {
    width: auto;
    padding: 128px 84px 0;
}

.poster-content h2 {
    width: 100%;
    color: var(--dark);
    background: transparent;
    border: 0;
    font-size: 76px;
    font-weight: 900;
    letter-spacing: 0;
    line-height: 1.05;
    margin: 0 0 74px;
    padding: 0;
    text-align: center;
    text-shadow: none;
}

.poster-content h2::after {
    content: "";
    display: block;
    width: 168px;
    height: 8px;
    background: var(--primary);
    margin: 28px auto 0;
    box-shadow: 44px 0 0 var(--dark);
}

.poster-message-box {
    height: 670px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 2px solid rgba(15, 23, 43, .08);
    border-top: 12px solid var(--primary);
    box-shadow: 0 22px 55px rgba(15, 23, 43, .08);
    padding: 70px 68px;
    overflow: hidden;
}

.poster-message {
    color: #2f3340;
    font-size: 44px;
    font-weight: 700;
    line-height: 1.42;
    text-align: center;
    white-space: pre-line;
    max-height: 100%;
    margin: 0;
    overflow: hidden;
}

.poster-message.short {
    font-size: 56px;
    line-height: 1.28;
}

.poster-message.medium {
    font-size: 42px;
}

.poster-message.long {
    font-size: 32px;
    line-height: 1.4;
}

.poster-message.very-long {
    font-size: 27px;
    line-height: 1.34;
}

.poster-message.tiny {
    font-size: 23px;
    line-height: 1.3;
}

.poster-message.empty {
    color: #8a8f9c;
    font-weight: 600;
}

.poster-info {
    position: absolute;
    left: 84px;
    right: 84px;
    bottom: 176px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
}

.poster-info.count-1 {
    grid-template-columns: minmax(0, 1fr);
}

.poster-info.count-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.poster-info div {
    min-height: 116px;
    display: grid;
    grid-template-rows: 22px 1fr;
    gap: 10px;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 43, .1);
    border-bottom: 5px solid var(--primary);
    padding: 16px 18px 18px;
}

.poster-info i {
    color: var(--primary);
    font-size: 28px;
    flex: 0 0 auto;
    margin: 0;
}

.poster-info span {
    min-height: 58px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: var(--dark);
    line-height: 1.25;
    text-align: center;
}

.poster-info small {
    color: #6b7280;
    font-size: 16px;
    font-weight: 900;
    letter-spacing: 0;
    text-transform: uppercase;
    min-height: 20px;
    text-align: center;
}

.poster-info strong {
    color: var(--dark);
    font-size: 23px;
    font-weight: 900;
    line-height: 1.18;
    display: flex;
    align-items: center;
}

.poster-footer {
    position: absolute;
    left: 84px;
    right: 84px;
    bottom: 54px;
    display: flex;
    align-items: center;
    gap: 18px;
    color: #666565;
    background: #fff;
    border: 1px solid rgba(15, 23, 43, .08);
    padding: 16px 18px;
    font-size: 17px;
}

.poster-footer img {
    width: 74px;
    height: 74px;
    flex: 0 0 74px;
    border-radius: 50%;
    object-fit: cover;
}

.poster-footer div {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
}

.poster-footer span {
    max-width: 650px;
}

.poster-footer strong {
    color: var(--dark);
    white-space: nowrap;
}

@media (max-width: 991.98px) {
    .announcement-editor {
        padding: 36px 0 96px;
    }

    .tool-layout {
        grid-template-columns: 1fr;
    }

    .tool-container {
        padding-left: 12px;
        padding-right: 12px;
    }

    .form-grid {
        grid-template-columns: 1fr;
    }

    .preview-shell {
        padding: 12px;
    }
}
</style>
